import {
  azureStorageService,
  isMissingBlobNotFoundError,
} from "./azureStorageService";
import { timetableDB } from "../db/indexedDb";
import { fetchEventsICS } from "./eventsService";
import { runtimeConfig } from "../config/runtime";

// Default interval in case timesRefreshInterval is missing
const DEFAULT_REFRESH_INTERVAL_MINS = 60;

// Maximum sync queue size to prevent unbounded growth during runaway loops
const MAX_SYNC_QUEUE_SIZE = 10;

export async function startPrayerTimeSync(
  masjidId: string | number,
  onUpdate: (data: any) => void,
  timesRefreshInterval?: number,
  showEvents?: boolean,
  onScreenRefresh?: () => void,
) {
  let syncQueue: { year: string; month: string }[] = [];
  let isSyncing = false;
  let syncTimer: number | undefined;

  // Cache of months that returned 404 — avoids hammering for blobs that don't exist yet
  const notFoundCache = new Map<string, number>();
  const NOT_FOUND_COOLDOWN_MS = 6 * 60 * 60 * 1000; // 6 hours

  // Azure Best Practice: Use provided value or fallback to default (in minutes)
  const refreshIntervalMs =
    (timesRefreshInterval || DEFAULT_REFRESH_INTERVAL_MINS) * 60 * 1000;

  console.log(
    `Prayer times sync interval set to: ${refreshIntervalMs / 60000} minutes`,
  );

  // ── Core sync logic ───────────────────────────────────────────────────

  async function sync() {
    // Guard: skip if already syncing (prevents concurrent bursts on rapid reconnect)
    if (isSyncing) {
      console.log("Sync already in progress, skipping");
      return;
    }
    isSyncing = true;

    try {
      const now = new Date();
      const year = now.getFullYear().toString();
      const month = (now.getMonth() + 1).toString();
      const normalizedMonth = String(Number(month));

      // When offline, serve from IndexedDB cache directly — no network calls
      if (!navigator.onLine) {
        console.log("Device is offline, serving cached prayer times");
        const cached = await timetableDB.getTimetable(
          year,
          normalizedMonth,
          masjidId.toString(),
        );
        // Also with no month saved: the day may have rolled into it, and the screen
        // must stop showing the previous day's times
        onUpdate(cached);
        return;
      }

      // The upcoming-changes panel looks ahead up to 7 days, so once that
      // window crosses the month boundary the next month must be ETag-checked
      // too — otherwise an edit to next month's times (made after the day-27
      // startup prefetch) shows stale phantom changes until the next reboot
      const lookahead = new Date(now);
      lookahead.setDate(lookahead.getDate() + 7);
      const lookaheadMonth = (lookahead.getMonth() + 1).toString();
      const lookaheadYear = lookahead.getFullYear().toString();
      const needsNextMonthSync =
        lookaheadMonth !== month || lookaheadYear !== year;

      // Sync current month
      await syncMonth(year, normalizedMonth, masjidId.toString());

      if (needsNextMonthSync) {
        console.log(`Syncing next month's data for the lookahead window`);
        await syncMonth(lookaheadYear, lookaheadMonth, masjidId.toString());
      }

      // Always notify so that both today and tomorrow get updated
      const updated = await timetableDB.getTimetable(
        year,
        normalizedMonth,
        masjidId.toString(),
      );
      onUpdate(updated);

      // Also sync events on the same schedule (only when events are enabled)
      if (showEvents) {
        try {
          await fetchEventsICS(
            Number(masjidId),
            (timesRefreshInterval || DEFAULT_REFRESH_INTERVAL_MINS) * 60,
          );
        } catch (eventsError) {
          console.error("Failed to sync events:", eventsError);
        }
      }
    } catch (error) {
      console.error("Error syncing prayer times:", error);
    } finally {
      isSyncing = false;
    }
  }

  // Extract month syncing logic into a separate function
  async function syncMonth(year: string, month: string, masjidId: string) {
    // Skip months that recently returned 404 — blob doesn't exist yet
    const nfKey = `${year}-${month}`;
    const lastNotFound = notFoundCache.get(nfKey);
    if (lastNotFound && Date.now() - lastNotFound < NOT_FOUND_COOLDOWN_MS) {
      console.log(`Skipping ${nfKey}: returned 404 recently, cooldown active`);
      return false;
    }

    const cached = await timetableDB.getTimetable(year, month, masjidId);
    const cachedETag = cached?.eTag || null;

    const blobPath = `timetables/${masjidId}/${year}/${month}.json.gz`;

    try {
      const { data: latest, eTag: latestETag } =
        await azureStorageService.getPrayerTimesFromBlob(masjidId, blobPath);

      // Extract the actual days array from the blob data
      const daysArray = Array.isArray(latest) ? latest : latest.days;

      if (latestETag !== cachedETag) {
        await timetableDB.saveTimetable(year, month, masjidId, {
          data: daysArray,
          eTag: latestETag,
        });
        console.log(`Prayer times updated for ${masjidId} (${year}-${month})`);
        return true;
      } else {
        console.log(
          `Prayer times unchanged for ${masjidId} (${year}-${month})`,
        );
        return false;
      }
    } catch (error: any) {
      console.error(`Error syncing ${year}-${month}:`, error);

      // 404 means the blob doesn't exist yet — cache the result, don't retry
      if (isMissingBlobNotFoundError(error)) {
        notFoundCache.set(nfKey, Date.now());
        console.log(
          `Month ${year}-${month} blob missing, treating as non-fatal and setting cooldown for 6h`,
        );
        return false;
      }

      // Non-404 errors: queue for retry when back online (capped)
      if (syncQueue.length < MAX_SYNC_QUEUE_SIZE) {
        const alreadyQueued = syncQueue.some(
          (q) => q.year === year && q.month === month,
        );
        if (!alreadyQueued) {
          syncQueue.push({ year, month });
          console.log(`Added to sync queue: ${year}-${month}`);
        }
      }
      return false;
    }
  }

  // ── Proactive data prefetching ────────────────────────────────────────

  async function prefetchFutureMonths() {
    try {
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear();
      const currentDay = now.getDate();

      const NEXT_MONTH_THRESHOLD = 27;

      for (let i = 0; i < 3; i++) {
        const targetDate = new Date(currentYear, currentMonth + i - 1, 1);
        const year = targetDate.getFullYear().toString();
        const month = (targetDate.getMonth() + 1).toString();

        if (i > 0 && currentDay < NEXT_MONTH_THRESHOLD) {
          console.log(
            `Skipping prefetch for ${year}-${month} as current day ${currentDay} is before threshold ${NEXT_MONTH_THRESHOLD}`,
          );
          continue;
        }

        console.log(`Prefetching prayer times for ${year}-${month}`);
        try {
          await syncMonth(year, month, masjidId.toString());
        } catch (err: any) {
          if (err?.message?.includes("404")) {
            console.log(
              `Month ${year}-${month} not available yet, stopping prefetch`,
            );
            break; // No point fetching further into the future
          } else if (err?.message?.includes("403")) {
            console.warn(
              `Sync for ${year}-${month} failed with 403; skipping further retries this cycle.`,
            );
          } else {
            console.error(`Error prefetching ${year}-${month}:`, err);
          }
        }
      }

      console.log("Prefetching complete");
    } catch (error) {
      console.error("Error prefetching future prayer times:", error);
    }
  }

  // ── Event handlers (single registration, both cleaned up in stop()) ───

  function handleOffline() {
    console.log("Network connection lost, switching to offline mode");
    // Serve cached data immediately (fire-and-forget, non-blocking)
    const now = new Date();
    const year = now.getFullYear().toString();
    const month = (now.getMonth() + 1).toString();
    timetableDB
      .getTimetable(year, month, masjidId.toString())
      .then((cached) => {
        if (cached) {
          onUpdate(cached);
        }
      })
      .catch((err) => console.error("Error reading cache on offline:", err));
  }

  function handleOnline() {
    // Guard: if WiFi flapped and we're already offline again, bail out
    if (!navigator.onLine) return;
    console.log("Network reconnected, processing sync queue");
    // Use isSyncing guard inside sync() to prevent concurrent bursts
    if (syncQueue.length > 0) {
      console.log(`Processing ${syncQueue.length} pending sync operations`);
      const queueCopy = [...syncQueue];
      syncQueue = [];
      (async () => {
        if (isSyncing) return;
        isSyncing = true;
        try {
          for (const item of queueCopy) {
            if (!navigator.onLine) break; // stop if we went offline again
            await syncMonth(item.year, item.month, masjidId.toString());
          }
          // After processing queue, do a normal sync to update UI
          isSyncing = false;
          await sync();
        } catch (err) {
          console.error("Error processing sync queue:", err);
          isSyncing = false;
        }
      })();
    } else {
      sync();
    }
  }

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  // ── Run initial sync ──────────────────────────────────────────────────

  await sync();

  if (navigator.onLine) {
    prefetchFutureMonths();
  }

  console.log("Prayer time sync: using polling interval", refreshIntervalMs);
  syncTimer = window.setInterval(sync, refreshIntervalMs);

  // ── Cleanup function (removes ALL listeners + timer) ──────────────────

  return {
    stop: () => {
      console.log("Stopping prayer time sync");
      if (syncTimer) {
        clearInterval(syncTimer);
        syncTimer = undefined;
      }
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      console.log("Prayer time sync stopped");
    },
  };
}
